import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { CONTACT, NAV_LINKS } from "@/data/site";
import { scrollTo } from "./Reveal";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (!href.startsWith("#")) return navigate(href);
    if (pathname !== "/") return navigate(`/${href}`);
    scrollTo(href);
  };

  const onLending = pathname === "/private-lending";

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 ${scrolled || open ? "glass" : ""}`}
      data-testid="site-nav"
    >
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12 h-20 flex items-center justify-between">
        <a href="/" onClick={go("#top")} className="group flex items-baseline gap-3" data-testid="nav-logo">
          <span className="font-serif text-2xl tracking-tight text-ivory">Bossard</span>
          <span className="eyebrow hidden sm:inline text-ivory/60 group-hover:text-gold transition-colors">Investment Group</span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href.startsWith("#") ? `/${l.href}` : l.href}
              onClick={go(l.href)}
              className={`link-underline text-[13px] tracking-wide transition-colors hover:text-ivory ${!l.href.startsWith("#") && onLending ? "text-gold" : "text-ivory/70"}`}
              data-testid={`nav-link-${l.href.replace(/^[#/]/, "")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-6">
          <a href={CONTACT.phoneHref} className="flex items-center gap-2 text-[13px] text-ivory/70 hover:text-gold transition-colors" data-testid="nav-phone">
            <Phone size={13} strokeWidth={1.5} /> {CONTACT.phone}
          </a>
          <a
            href={CONTACT.calendly}
            target="_blank"
            rel="noreferrer"
            className="btn-gold border border-gold text-gold text-[12px] tracking-[0.18em] uppercase px-5 py-2.5"
            data-testid="nav-calendly-btn"
          >
            Book a Call
          </a>
        </div>

        <button className="lg:hidden text-ivory p-2" onClick={() => setOpen((o) => !o)} aria-label="Menu" data-testid="nav-menu-toggle">
          {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden glass h-[calc(100vh-5rem)]"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 pb-8 pt-6 flex flex-col gap-6">
              {NAV_LINKS.map((l) => (
                <a key={l.href} href={l.href.startsWith("#") ? `/${l.href}` : l.href} onClick={go(l.href)} className="font-serif text-3xl text-ivory/90" data-testid={`nav-mobile-link-${l.href.replace(/^[#/]/, "")}`}>
                  {l.label}
                </a>
              ))}
              <a href={CONTACT.phoneHref} className="text-sm text-ivory/60" data-testid="nav-mobile-phone">{CONTACT.phone}</a>
              <a href={CONTACT.calendly} target="_blank" rel="noreferrer" className="mt-2 border border-gold text-gold text-center text-xs tracking-[0.18em] uppercase py-3" data-testid="nav-mobile-calendly-btn">
                Book a Call
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
